import { Flex, Spacer } from '@lokalise/louis';

import useSelector from '@app/hooks/useSelector';

import { getRepositories } from '../../reducers/downloadData/selectors';
import type { Repositories, Triggers as TriggersType } from '../../types';
import ExportAccordion from '../ExportAccordion';
import CommitTemplateInput from './components/CommitTemplateInput';
import FilterRepositoriesInput from './components/FilterRepositoriesInput';
import type { Options } from './components/FilterRepositoriesInput';
import TriggersCheckboxes from './components/TriggersCheckboxes';

const getCheckedTriggers = (triggers: TriggersType) => {
    const entries = Object.entries(triggers);

    return entries.filter(([, value]) => value).map(([trigger]) => trigger.replace('triggers', '').toLowerCase());
};

const prepareValue = (value: string): Options =>
    value
        .split(',')
        .filter((tag) => tag !== '')
        .map((item) => ({ value: item, label: item }));

const prepareOptions = (
    repositories: Repositories | undefined,
    checkedTriggers: string[],
): { value: string; label: string }[] => {
    const repositoryEntries = Object.entries(repositories ?? {});

    return (
        repositoryEntries
            .filter(([trigger]) => checkedTriggers.includes(trigger.replace('-', '')))
            .flatMap(([, repos]) => repos)
            .map((item) => ({ value: item as string, label: item as string })) ?? []
    );
};

const prepareFilterRepositoriesOutgoingValue = (value: Options) => value.map((item) => item.value).join(',');

type Value = {
    triggers: TriggersType;
    filterRepositories: string;
    commitTemplate: string;
    defaultBitbucketReviewers: boolean;
};

export const isInputsVisible = (triggers: TriggersType) =>
    triggers.triggersGithub ||
    triggers.triggersGithubEnterprise ||
    triggers.triggersGitlab ||
    triggers.triggersBitbucket ||
    triggers.triggersBitbucketEnterprise ||
    triggers.triggersAzure;

export interface TriggersProps {
    value: Value;
    onChange: (value: Value) => void;
    projectId: string;
}

const Triggers = ({ value, onChange, projectId }: TriggersProps) => {
    const repositories = useSelector(getRepositories);

    const filterRepositoriesValue = prepareValue(value.filterRepositories);

    const handleTriggersChange = (val: { triggers: TriggersType; defaultBitbucketReviewers: boolean }) => {
        const filterRepositoriesOptions = prepareOptions(repositories, getCheckedTriggers(val.triggers));
        const filterRepositoriesOptionValues = filterRepositoriesOptions.map((item) => item.value);

        const newFilterRepositoriesValue = filterRepositoriesValue.filter((item) =>
            filterRepositoriesOptionValues.includes(item.value),
        );

        onChange({
            ...value,
            triggers: val.triggers,
            defaultBitbucketReviewers: val.defaultBitbucketReviewers,
            filterRepositories: prepareFilterRepositoriesOutgoingValue(newFilterRepositoriesValue),
        });
    };

    return (
        <ExportAccordion heading={<b>App triggers</b>} name="triggers" projectId={projectId}>
            <Spacer top={4} data-metric="download-app-trigger-apps">
                <TriggersCheckboxes
                    value={{
                        triggers: {
                            triggersAmazons3: value.triggers.triggersAmazons3,
                            triggersGcs: value.triggers.triggersGcs,
                            triggersGithub: value.triggers.triggersGithub,
                            triggersGithubEnterprise: value.triggers.triggersGithubEnterprise,
                            triggersGitlab: value.triggers.triggersGitlab,
                            triggersBitbucket: value.triggers.triggersBitbucket,
                            triggersBitbucketEnterprise: value.triggers.triggersBitbucketEnterprise,
                            triggersAzure: value.triggers.triggersAzure,
                        },
                        defaultBitbucketReviewers: value.defaultBitbucketReviewers,
                    }}
                    onChange={handleTriggersChange}
                />
            </Spacer>
            {isInputsVisible(value.triggers) && (
                <Spacer top={6} data-metric="download-app-trigger-commit">
                    <Flex gap={4} direction="column">
                        <FilterRepositoriesInput
                            options={prepareOptions(repositories, getCheckedTriggers(value.triggers))}
                            value={filterRepositoriesValue}
                            onChange={(filterRepositories) =>
                                onChange({
                                    ...value,
                                    filterRepositories: prepareFilterRepositoriesOutgoingValue(filterRepositories),
                                })
                            }
                        />
                        <CommitTemplateInput
                            value={value.commitTemplate}
                            onChange={(commitTemplate) =>
                                onChange({
                                    ...value,
                                    commitTemplate,
                                })
                            }
                        />
                    </Flex>
                </Spacer>
            )}
        </ExportAccordion>
    );
};

export default Triggers;
